import { t } from 'ttag';

import { UNKNOWN_ERROR_MESSAGE } from '@constants';
import { notifyError, notifySuccess } from '@utils/notifications';
import { useUserSubscription } from '@hook/useUserSubscription';
import { ShafaLoader } from '@ui/ShafaLoader';
import { UserProfile } from '@components/UserProfile';
import { sendSellerSubscribeEvent } from '@utils/analytics';

import {
  useUserProfileQuery,
  useUserProfileViewerQuery,
} from './graphql.v3.generated';

type UserProfileContainerProps = {
  username: string,
  className?: string,
};

export const UserProfileContainer: React.FC<UserProfileContainerProps> = ({
  username,
  className,
}) => {
  const {
    loading: followLoading,
    handleSubscribe,
    handleUnsubscribe,
  } = useUserSubscription();
  const { data, loading } = useUserProfileQuery({
    variables: { username },
    context: { batch: true },
  });
  const {
    data: viewerData,
    loading: viewerLoading,
  } = useUserProfileViewerQuery({
    context: { batch: true, ignoreAuthError: true },
  });

  if (!data?.user && !loading) {
    // TODO: add 404
    return null;
  }

  const handleFollowToggle = () => {
    if (data?.user) {
      if (data.user.isSubscribed) {
        handleUnsubscribe({
          userId: data.user.id,
          onSuccess: () => notifySuccess(t`Ви успішно відписалися`),
          onError: () => notifyError(UNKNOWN_ERROR_MESSAGE()),
        });
      } else {
        handleSubscribe({
          userId: data.user.id,
          onSuccess: () => {
            sendSellerSubscribeEvent({ referrer: 'user_profile' });
            notifySuccess(t`Ви успішно підписалися`);
          },
          onError: () => notifyError(UNKNOWN_ERROR_MESSAGE()),
        });
      }
    }
  };

  return (
    <>
      <ShafaLoader
        in={loading || viewerLoading}
        delay={400}
        shadowOverlay={true}
      />
      {data?.user && (
        <UserProfile
          shopName={data.user.shopName}
          id={data.user.id}
          username={data.user.username}
          thumbnail={data.user.thumbnail}
          firstName={data.user.firstName}
          lastName={data.user.lastName}
          showRealName={data.user.showRealName}
          followersCount={data.user.followersCount}
          sellerRatingInfo={data.user.sellerRatingInfo}
          soldProducts={data.user.soldProducts}
          middleResponseTime={data.user.middleResponseTime}
          lastVisit={data.user.lastVisit}
          city={data.user.city}
          gender={data.user.gender}
          deltaDateJoined={data.user.deltaDateJoined}
          viewer={viewerData?.viewer}
          isSubscribed={data.user.isSubscribed}
          isTopSeller={data.user.isTopSeller}
          isFollowLoading={followLoading}
          onFollowToggle={handleFollowToggle}
          fraudWarning={data.user.fraudWarning}
          ban={data.user.ban}
          productsCount={data.user.productsCount}
          hasRecentActivity={data.user.hasRecentActivity}
          orderRate={data.user.orderRate}
          className={className}
        />
      )}
    </>
  );
};
